import { useState, useCallback, useRef, useEffect } from 'react';
import { useFocusEffect } from '@react-navigation/native';

import { useAppDispatch } from '@store/hooks/useAppDispatch';
import { productionBatchesSearch } from '@reducers/search';
import { BATCH_LIST_SELECT, TABLE_NAMES } from '@v2/pages/common/constants/base.constants';
import { isPhone } from '@helpers';
import { ProductionBatch } from '@interfaces/search';
import { BatchSearchResponse, UseAllBatchesParams } from '@components/operatorPanel/components/OperatorPanelInformation/components/OperatorPanelInformationSearchHistoryScreen/types';
import { useGridPersistence } from '@v2/pages/common/hooks/useGridPersistence';

const getTopAmount = () => (isPhone ? 15 : 100);

export const useAllBatches = ({ machineFilter, isFilterReady = true, isEmptyFilter = false }: UseAllBatchesParams) => {
  const [searchText, setSearchText] = useState('');
  const [batches, setBatches] = useState<ProductionBatch[]>([]);
  const [totalCount, setTotalCount] = useState(0);
  const [loading, setLoading] = useState(true);
  const [hasMore, setHasMore] = useState(true);

  const dispatch = useAppDispatch();

  const skipRef = useRef(0);
  const isLoadingMoreRef = useRef(false);
  const currentSearchRef = useRef('');

  const machineFilterRef = useRef(machineFilter);
  const hasFetchedRef = useRef(false);

  useEffect(() => {
    machineFilterRef.current = machineFilter;
  }, [machineFilter]);

  const { orderByRef, gridODataFilterRef, handleGridSearchParams } = useGridPersistence(TABLE_NAMES.operatorPanelSearchHistory, {
    defaultOrderBy: ['EndedOn desc'],
  });

  const fetchBatches = useCallback(
    async (skip: number, search: string, isLoadMore = false) => {
      if (isEmptyFilter) {
        setBatches([]);
        setTotalCount(0);
        setHasMore(false);
        setLoading(false);
        return [];
      }

      const machineODataFilter = machineFilterRef.current;
      const gridFilter = gridODataFilterRef.current;
      const combinedODataFilter = [machineODataFilter, gridFilter].filter(Boolean).join(' and ') || undefined;

      try {
        const res: BatchSearchResponse = await dispatch(
          productionBatchesSearch({
            data: { top: getTopAmount(), skip },
            filter: {
              orderBy: orderByRef.current,
              search,
              oDataFilter: combinedODataFilter,
            },
            select: BATCH_LIST_SELECT,
            resetHistory: skip === 0,
            ignoreHistory: true,
            signal: undefined,
          })
        );

        if (res?.searchResult) {
          const mappedBatches = res.searchResult;

          if (isLoadMore) {
            setBatches((prev) => [...prev, ...mappedBatches]);
          } else {
            setBatches(mappedBatches);
          }

          setTotalCount(res.totalResultCount);
          setHasMore(skip + mappedBatches.length < res.totalResultCount);

          return mappedBatches;
        }

        return [];
      } catch (error) {
        console.error('Error fetching batches:', error);
        return [];
      }
    },
    [dispatch, isEmptyFilter]
  );

  const loadBatches = useCallback(async () => {
    setLoading(true);
    skipRef.current = 0;
    currentSearchRef.current = '';
    await fetchBatches(0, '');
    setLoading(false);
  }, [fetchBatches]);

  // Single fetch trigger - waits for filter to be ready
  useFocusEffect(
    useCallback(() => {
      if (!isFilterReady) return;

      loadBatches();
      hasFetchedRef.current = true;
    }, [loadBatches, isFilterReady])
  );

  // Refetch when machineFilter changes (only after initial fetch)
  useEffect(() => {
    if (!hasFetchedRef.current || !isFilterReady) return;

    setLoading(true);
    skipRef.current = 0;
    currentSearchRef.current = '';
    fetchBatches(0, '').then(() => setLoading(false));
  }, [machineFilter]);

  const handleSearch = useCallback(async () => {
    setLoading(true);
    skipRef.current = 0;
    currentSearchRef.current = searchText;
    await fetchBatches(0, searchText);
    setLoading(false);
  }, [fetchBatches, searchText]);

  const clearSearch = useCallback(async () => {
    setSearchText('');
    setLoading(true);
    skipRef.current = 0;
    currentSearchRef.current = '';
    await fetchBatches(0, '');
    setLoading(false);
  }, [fetchBatches]);

  const gridSearch = useCallback(
    async ({ orderBy, oDataFilter }: { orderBy?: string[]; oDataFilter?: string | null; signal?: AbortSignal }) => {
      handleGridSearchParams({ orderBy, oDataFilter });
      setLoading(true);
      skipRef.current = 0;
      await fetchBatches(0, currentSearchRef.current);
      setLoading(false);
    },
    [fetchBatches, handleGridSearchParams]
  );

  const loadMore = useCallback(async () => {
    if (isLoadingMoreRef.current || !hasMore) return;

    isLoadingMoreRef.current = true;
    const nextSkip = skipRef.current + getTopAmount();
    skipRef.current = nextSkip;
    await fetchBatches(nextSkip, currentSearchRef.current, true);
    isLoadingMoreRef.current = false;
  }, [fetchBatches, hasMore]);

  return {
    searchText,
    setSearchText,
    batches,
    totalCount,
    loading,
    hasMore,
    handleSearch,
    clearSearch,
    loadMore,
    gridSearch,
  };
};
